import React from 'react';
import { AreaChartOutlined, BookOutlined, CommentOutlined, LogoutOutlined } from '@ant-design/icons';
import { Button, Image, Typography } from 'antd';
import IconRound from 'assets/logos/icon-round.svg?react';
import { useAuth } from 'auth/AuthContext';
import { colors, space } from 'design';
import useIsSmallScreen from 'hooks/useIsSmallScreen';
import { createUseStyles } from 'react-jss';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import { RouteInfo, routes } from '../../services/RouteService';

const useStyles = createUseStyles({
  navBarContainer: {
    background: colors.surface.base,
    alignItems: 'center',
    padding: 10,
    paddingBottom: 'calc(var(--gg-safe-area-bottom) / 2 + 20px)',
  },
  buttonsContainer: {
    height: 60,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  buttonsCenterContainer: {
    height: 60,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-around',
  },
  container: {
    /*
     * The nav items are `Button`s, so they inherit the theme's `paddingInline`
     * — which is sized for a real button with a label, not for four icons
     * sharing a 390px bar. At the theme value the four of them are 43px wider
     * than the viewport, which gives the whole document a horizontal scroll.
     *
     * They also must be allowed to shrink: a flex item defaults to
     * `min-width: auto`, so without this the labels set the floor.
     */
    paddingInline: space.xs,
    minWidth: 0,
    flexShrink: 1,
    '& span.ant-btn-icon': {
      alignSelf: 'center',
    },
  },
  // Inactive items read as the turquoise family too, at the readable weight —
  // `primaryStrong`, not `palette.teal[300]` — so the active label (paon)
  // stays the one text that reads as dark/saturated. The icon pairing is the
  // other way round: see the `iconColor` in `renderIcon` below.
  navigationText: {
    color: colors.brand.primaryStrong,
  },
  navigationTextActive: {
    color: colors.brand.accent,
  },
  buttonMobile: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  mobileText: {
    fontSize: 12,
  },
  logoutIcon: {
    marginTop: 4,
    fontSize: 26,
  },
});

export default function BottomNavBar() {
  // Hooks
  const navigate = useNavigate();
  const styles = useStyles();
  const { logout } = useAuth();
  const { isSmallScreen } = useIsSmallScreen();
  const location = useLocation();

  const onClick = (route: string) => {
    if (route === routes.logout.route) {
      logout();
    } else if (location.pathname === route) {
      // if we are already on the route, scroll to top
      navigate(route, { replace: true, state: { ...location.state, scrollValue: 0 } });
    } else {
      navigate(route);
    }
  };

  function renderIcon(src: any, size: number, svgIcon: any, active?: boolean) {
    if (svgIcon) {
      const iconColor = active === true ? colors.brand.primaryStrong : colors.brand.primary;
      return (
        // `aria-hidden`: every nav item already renders its label as text, so the
        // icon is purely decorative. antd icons carry an `aria-label` of their own
        // ("comment", "area-chart", "book"), which without this gets folded into
        // the button's accessible name — screen readers announce "comment
        // Messages", and `get_by_role("button", name="OK")` starts matching
        // "book M'informer".
        <div aria-hidden style={{ height: size, fontSize: size * 0.75, color: iconColor }}>
          {React.cloneElement(svgIcon, { fill: iconColor })}
        </div>
      );
    }
    return <Image alt="icon" height={size} width={size} preview={false} src={src} />;
  }

  function renderNavButton(params: {
    svgIcon?: any;
    routeInfo: RouteInfo;
    onClickFn?: () => void;
    isCenterIcon?: boolean;
    routesGroup?: string;

    // these are deprecated
    iconSrc?: any;
    selectedIconSrc?: any;
  }) {
    const centerButtonStyle = {
      borderWidth: '10px',
      borderColor: colors.surface.base,
      borderRadius: '35px',
      height: 'auto',
      padding: 0,
    };

    const matchingRoute = Object.values(routes).find(route => matchPath(location.pathname, route.route));

    const highlightIcon = matchingRoute && matchingRoute.routesGroup === params.routesGroup;
    const iconSource = highlightIcon ? params.selectedIconSrc : params.iconSrc;

    return (
      <Button
        className={styles.container}
        type="link"
        icon={!isSmallScreen && renderIcon(iconSource, 35, params.svgIcon, highlightIcon)}
        onClick={() => {
          if (params.onClickFn) {
            params.onClickFn();
          } else {
            onClick(params.routeInfo.route);
          }
        }}
        style={params.isCenterIcon ? centerButtonStyle : {}}
      >
        {isSmallScreen ? (
          <div className={styles.buttonMobile}>
            {renderIcon(iconSource, params.isCenterIcon ? 50 : 30, params.svgIcon, highlightIcon)}
            {params.isCenterIcon ? null : (
              <Typography.Text className={styles.mobileText}>
                <span className={highlightIcon ? styles.navigationTextActive : styles.navigationText}>
                  {params.routeInfo.label}
                </span>
              </Typography.Text>
            )}
          </div>
        ) : (
          <span className={highlightIcon ? styles.navigationTextActive : styles.navigationText}>
            {params.routeInfo.label}
          </span>
        )}
      </Button>
    );
  }

  return (
    <nav className={styles.navBarContainer}>
      <div className={styles.buttonsCenterContainer}>
        {renderNavButton({
          svgIcon: <IconRound style={{ marginTop: '2px' }} width="21px" height="21px" />,
          routeInfo: routes.home,
          routesGroup: 'home',
        })}
        {renderNavButton({
          svgIcon: <CommentOutlined />,
          routeInfo: routes.messagingHome,
          routesGroup: 'messaging',
        })}
        {renderNavButton({
          routeInfo: routes.symptomTracking,
          routesGroup: 'tracking',
          svgIcon: <AreaChartOutlined />,
        })}
        {renderNavButton({
          svgIcon: <BookOutlined style={{ fontSize: '0.9em' }} />,
          routeInfo: routes.info,
          routesGroup: 'info',
        })}
        {!isSmallScreen && (
          <Button className={styles.container} type="link">
            <LogoutOutlined
              className={styles.logoutIcon}
              onClick={() => {
                logout();
              }}
            />
          </Button>
        )}
      </div>
    </nav>
  );
}
